import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Scene } from "@/components/tour/Scenes";
import { useTour } from "@/context/TourContext";
import { ChevronLeft, ChevronRight, Check, Rocket, FileText, Receipt, Lock, Archive, LifeBuoy } from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = { rocket: Rocket, "file-text": FileText, receipt: Receipt, lock: Lock, archive: Archive };

/**
 * Animert gjennomgang av én Hjelp-seksjon (én slide per steg). Vises automatisk første gang en modul
 * åpnes, og kan spilles av igjen fra Hjelp. Lukking/hopp over markerer gjennomgangen som sett.
 */
export const WalkthroughDialog = () => {
  const { active, guide, closeTour } = useTour();
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const section = useMemo(() => guide?.sections?.find((s) => s.key === active?.key) || null, [guide, active]);
  const steps = section?.steps || [];
  const total = steps.length;
  const step = steps[index];
  const last = index === total - 1;

  useEffect(() => { setIndex(0); setDir(1); }, [active?.key]);

  const go = (delta) => {
    const next = Math.min(total - 1, Math.max(0, index + delta));
    if (next !== index) { setDir(delta > 0 ? 1 : -1); setIndex(next); }
  };

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); if (last) closeTour(true); else go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active || !section || !step) return null;
  const Icon = ICONS[section.icon] || LifeBuoy;
  const variants = reduced ? { enter: { opacity: 0 }, center: { opacity: 1 }, exit: { opacity: 0 } } : {
    enter: (d) => ({ x: d > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -40 : 40, opacity: 0 }),
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) closeTour(false); }}>
      <DialogContent className="bg-card p-0 overflow-hidden sm:max-w-3xl w-[calc(100vw-1.5rem)] gap-0" data-testid="walkthrough-dialog" aria-describedby="walkthrough-text">
        <div className="flex items-center gap-2 px-5 pt-4 pb-2">
          <span className="h-8 w-8 rounded-lg bg-accent text-accent-foreground flex items-center justify-center"><Icon className="h-4 w-4" /></span>
          <div className="min-w-0">
            <DialogTitle className="text-sm font-semibold leading-tight truncate" data-testid="walkthrough-title">{section.short ? `Slik bruker du ${section.short}` : section.title}</DialogTitle>
            <DialogDescription className="text-xs" data-testid="walkthrough-progress">Steg {index + 1} av {total}{active.source === "auto" ? " · vises kun første gang" : ""}</DialogDescription>
          </div>
        </div>

        {/* Fremdrift */}
        <div className="mx-5 h-1 rounded-full bg-secondary overflow-hidden" aria-hidden="true">
          <motion.div className="h-full bg-primary" initial={false} animate={{ width: `${((index + 1) / total) * 100}%` }} transition={{ duration: reduced ? 0 : 0.3 }} />
        </div>

        <div className="grid md:grid-cols-[1.15fr_1fr] gap-0 md:gap-6 px-5 pt-4 pb-2 md:min-h-[300px]">
          <div className="h-44 md:h-auto">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={`scene-${index}`} className="h-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.2 }}>
                <Scene name={step.scene} />
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="relative mt-4 md:mt-0 min-h-[120px]">
            <AnimatePresence mode="wait" custom={dir} initial={false}>
              <motion.div key={`text-${index}`} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: reduced ? 0 : 0.25 }} className="space-y-2" id="walkthrough-text" aria-live="polite">
                <div className="flex items-center gap-2">
                  <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center tnum">{index + 1}</span>
                  <h3 className="font-heading text-lg font-semibold leading-tight" data-testid="walkthrough-step-title">{step.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed" data-testid="walkthrough-step-text">{step.text}</p>
                {last && section.tip ? <p className="text-xs rounded-md bg-accent/70 border border-primary/20 px-3 py-2"><span className="font-medium">Tips:</span> {section.tip}</p> : null}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t bg-secondary/40">
          <Button variant="ghost" size="sm" onClick={() => closeTour(false)} data-testid="walkthrough-skip">{last ? "Lukk" : "Hopp over"}</Button>
          <div className="flex items-center gap-1.5" role="tablist" aria-label="Steg">
            {steps.map((_, i) => (
              <button key={i} type="button" role="tab" aria-selected={i === index} aria-label={`Steg ${i + 1}`} onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }} className={cn("h-2 rounded-full transition-[width,background-color] duration-200", i === index ? "w-5 bg-primary" : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/60")} data-testid={`walkthrough-dot-${i}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => go(-1)} disabled={index === 0} aria-label="Forrige" data-testid="walkthrough-prev"><ChevronLeft className="h-4 w-4" /><span className="hidden sm:inline ml-1">Tilbake</span></Button>
            {last ? (
              <Button size="sm" onClick={() => closeTour(true)} data-testid="walkthrough-finish"><Check className="h-4 w-4 mr-1" /> Ferdig</Button>
            ) : (
              <Button size="sm" onClick={() => go(1)} data-testid="walkthrough-next"><span className="mr-1">Neste</span><ChevronRight className="h-4 w-4" /></Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
